import React from 'react';
import { Button, Spinner } from '@odin-labs/components';
import { DocumentKey, FileType } from 'apollo/generated/client-operations';
import videoImage from 'images/video.png';
import { PencilIcon } from 'components/icons';
import { FilePreview, useFilePreview } from 'components/filePreview';
import { InfoList } from 'components/infoList';
import { PdfPreview } from 'components/thumbnail/PdfPreview';
import { InfoRow } from 'components/infoRow';
import {
  InputFile,
  SelfOnboardingServerFile,
  ReviewCompletedProfiledTabProps,
  SelfOnboardingDocumentFormData,
} from 'containers/selfOnboarding/types';
import { getDocumentInputsConfig } from 'containers/selfOnboarding/steps/documentStep';
import { getInfoRows } from './utils';

export const isServerFile = (
  file: InputFile,
): file is SelfOnboardingServerFile => file && !(file instanceof File);

export type ReviewCompletedProfileDocumentProps = {
  document: SelfOnboardingDocumentFormData;
  loading: boolean;
} & Pick<ReviewCompletedProfiledTabProps, 'navigation' | 'localize'>;

export function ReviewCompletedProfileDocument(
  props: ReviewCompletedProfileDocumentProps,
): React.ReactElement {
  const { document, loading, navigation, localize } = props;
  const { key: documentKey, workerDocTypeKey } = document;
  const { multiInstanceDocTitle } = document.documentType ?? {};
  const { goToStep } = navigation;
  const { titleCopy } = getDocumentInputsConfig(workerDocTypeKey);

  const documentDetailsRows = getInfoRows(document, localize);

  const file = document.frontFile;
  const { fileUrl, isLoading } = useFilePreview(file);

  const isJobsiteVideoDocument =
    workerDocTypeKey === DocumentKey.JobsiteSafetyVideo;

  const getPreviewContent = (): React.ReactNode => {
    if (isLoading) {
      return (
        <div className="odin-h-52 odin-flex odin-items-center odin-justify-center">
          <Spinner size="base" className="odin-text-odin-primary" />
        </div>
      );
    }

    const isPdf = isServerFile(file)
      ? file.fileType === FileType.Pdf
      : file?.type.includes('pdf');
    if (isPdf) {
      return <PdfPreview isThumbnail fileURL={fileUrl} alt={documentKey} />;
    }

    if (isJobsiteVideoDocument) {
      return (
        <FilePreview
          className="odin-w-full odin-h-52 odin-rounded-md odin-object-contain odin-cursor-pointer"
          alt={documentKey}
          fileUrl={videoImage}
          fileIcon={null}
          fileImgStyle={null}
          onClick={(): void => goToStep(documentKey)}
        />
      );
    }

    return (
      <FilePreview
        className="odin-w-full odin-h-52 odin-rounded-md odin-object-contain"
        alt={documentKey}
        file={file}
      />
    );
  };

  return (
    <div className="odin-rounded-md odin-shadow-odin-normal odin-p-6 odin-space-y-6">
      <div className="odin-flex odin-justify-between">
        <div>
          <h3 className="odin-text-base odin-font-medium odin-text-gray-900">
            {multiInstanceDocTitle ?? localize(titleCopy)}
          </h3>
          {isJobsiteVideoDocument && (
            <h4 className="odin-text-sm odin-text-gray-600">
              Onboarding Video
            </h4>
          )}
        </div>
        {!isJobsiteVideoDocument && (
          <Button
            theme="naked"
            size="sm"
            onClick={(): void => goToStep(documentKey)}
          >
            <PencilIcon className="odin-h-4 odin-w-4" />
          </Button>
        )}
      </div>
      {getPreviewContent()}
      <InfoList
        className="odin-space-y-3"
        loading={loading}
        noEdit
        noPadding
        noBorders
        noLgResponsiveness
      >
        {documentDetailsRows.map((row) => (
          <InfoRow key={row.label} {...row} />
        ))}
      </InfoList>
    </div>
  );
}
